/**
 * Where a connection points, as text that never carries a secret (Option C C2 review C2-03 and its r2 re-check).
 *
 * - `describeOracleDescriptor` PARSES an Oracle connect descriptor — nesting and quoting respected — and reads HOST and
 *   PORT only from an ADDRESS node (directly under DESCRIPTION or in its ADDRESS_LIST) and SERVICE_NAME / SID only from
 *   CONNECT_DATA; never from SECURITY or any other node, never from a quoted value. Anything it cannot read without
 *   guessing (an unterminated quote, unbalanced parentheses, trailing text, a duplicate key, a value outside a plain
 *   host / port / service shape) is the generic "TNS descriptor" with nothing extracted.
 * - `redactTargetSecrets` masks the value of every secret-named key in a target text or a driver's error, quote-aware
 *   (`"…"` / `'…'` with doubled quotes and backslash escapes, ODBC `{…}` with `}}`), a URL's password and an Oracle
 *   `user/password@`; a value it cannot delimit is masked to the end of the text (fails closed). It lives in
 *   `secretTextRedaction.ts` (security slice, 2026-09-28) and is re-exported here for its callers.
 * - `parseDescriptor` is exported for the export path's secret stripping (`stripTargetSecrets`).
 */
export { isSecretKeyName, redactTargetSecrets } from './secretTextRedaction';
import { MAX_REDACTION_INPUT } from './secretTextRedaction';

type DescriptorValue = { text: string; quoted: boolean };
/** One `(KEY=…)` node; `start` / `end` are the offsets of its `(` and just after its `)` in the parsed text. */
export type DescriptorNode = { key: string; value?: DescriptorValue; children?: DescriptorNode[]; start: number; end: number };

/** Deeper than any real descriptor (DESCRIPTION_LIST › DESCRIPTION › ADDRESS_LIST › ADDRESS › …); deeper is not one. */
const MAX_DESCRIPTOR_DEPTH = 32;

/**
 * The descriptor's top-level nodes, or null when it is not one well-formed descriptor. Iterative, with the nesting
 * bounded by `MAX_DESCRIPTOR_DEPTH` and the length by `MAX_REDACTION_INPUT` (security-slice review SBT-06: a recursive
 * parse overflowed the stack on a 10,000-deep descriptor).
 */
export function parseDescriptor(text: string): DescriptorNode[] | null {
  if (text.length > MAX_REDACTION_INPUT) return null;
  let i = 0;
  const skipSpace = () => {
    while (i < text.length && /\s/u.test(text[i])) i += 1;
  };
  const quoted = (): DescriptorValue | null => {
    const quote = text[i];
    let value = '';
    for (i += 1; i < text.length; i += 1) {
      if (text[i] === '\\' && i + 1 < text.length) {
        i += 1;
        value += text[i];
      } else if (text[i] === quote && text[i + 1] === quote) {
        i += 1;
        value += quote;
      } else if (text[i] === quote) {
        i += 1;
        return { text: value, quoted: true };
      } else {
        value += text[i];
      }
    }
    return null;
  };
  const leafValue = (): DescriptorValue | null => {
    if (text[i] === '"' || text[i] === "'") {
      const value = quoted();
      skipSpace();
      return value;
    }
    // A backslash escapes the next character in a bare value too (Oracle accepts `prefix\)rest`; review r3).
    const valueStart = i;
    while (i < text.length && text[i] !== ')' && !'("\''.includes(text[i])) i += text[i] === '\\' ? 2 : 1;
    return i > text.length ? null : { text: text.slice(valueStart, i).trim(), quoted: false };
  };

  const top: DescriptorNode[] = [];
  const open: Array<{ key: string; start: number; children: DescriptorNode[] }> = [];
  const attach = (node: DescriptorNode) => (open.length ? open[open.length - 1].children : top).push(node);
  for (;;) {
    skipSpace();
    if (open.length > 0 && text[i] === ')') {
      i += 1;
      const frame = open.pop()!;
      attach({ key: frame.key, children: frame.children, start: frame.start, end: i });
      continue;
    }
    if (text[i] !== '(') break;
    const start = i;
    i += 1;
    skipSpace();
    const keyStart = i;
    while (i < text.length && /[\w.-]/u.test(text[i])) i += 1;
    const key = text.slice(keyStart, i).toUpperCase();
    skipSpace();
    if (!key || text[i] !== '=') return null;
    i += 1;
    skipSpace();
    if (text[i] === '(') {
      if (open.length >= MAX_DESCRIPTOR_DEPTH) return null;
      open.push({ key, start, children: [] });
      continue;
    }
    const value = leafValue();
    if (!value || text[i] !== ')') return null;
    i += 1;
    attach({ key, value, start, end: i });
  }
  return open.length === 0 && top.length > 0 && i === text.length ? top : null;
}

const HOST_SHAPE = /^[\w.:%[\]-]+$/u;
const PORT_SHAPE = /^\d{1,5}$/u;
const SERVICE_SHAPE = /^[\w.$#-]+$/u;

/** The single child named `key` (undefined when absent), or null when there are several — ambiguous. */
function only(children: DescriptorNode[], key: string): DescriptorNode | undefined | null {
  const found = children.filter((child) => child.key === key);
  return found.length > 1 ? null : found[0];
}

/**
 * A value of the right shape — bare, or quoted with plain content (the quote is syntax, not a sign of a secret; Fable
 * security review S2) — null for anything else (nested, odd characters). Only ADDRESS / CONNECT_DATA values are read;
 * SECURITY never is, so a quoted wallet password cannot pose as a HOST.
 */
function plainValue(node: DescriptorNode | undefined | null, shape: RegExp): string | undefined | null {
  if (node === undefined) return undefined;
  if (node === null || !node.value || !shape.test(node.value.text)) return null;
  return node.value.text;
}

/** Where a descriptor dials, read by the parser from its first ADDRESS and its CONNECT_DATA only. */
export type OracleDescriptorAddress = { host: string; port?: string; service?: string; sid?: string };

function addressOf(description: DescriptorNode): OracleDescriptorAddress | null {
  const children = description.children ?? [];
  const addresses = children.flatMap((child) =>
    child.key === 'ADDRESS' ? [child] : child.key === 'ADDRESS_LIST' ? (child.children ?? []).filter((inner) => inner.key === 'ADDRESS') : []);
  const address = addresses[0]?.children;
  if (!address) return null;
  const host = plainValue(only(address, 'HOST'), HOST_SHAPE);
  const port = plainValue(only(address, 'PORT'), PORT_SHAPE);
  const connectData = only(children, 'CONNECT_DATA');
  if (!host || port === null || connectData === null) return null;
  const service = connectData?.children ? plainValue(only(connectData.children, 'SERVICE_NAME'), SERVICE_SHAPE) : undefined;
  const sid = connectData?.children ? plainValue(only(connectData.children, 'SID'), SERVICE_SHAPE) : undefined;
  if (service === null || sid === null) return null;
  return { host, ...(port ? { port } : {}), ...(service ? { service } : {}), ...(sid ? { sid } : {}) };
}

/**
 * The descriptor's first address and its service, or null when it names no host or cannot be read without guessing
 * (the same rules as `describeOracleDescriptor`). The connection dialog names a TNS-descriptor record by its `host`
 * (security slice 2026-09-28 — a regex scan there could pick a quoted `(HOST=…)` out of SECURITY).
 */
export function oracleDescriptorAddress(descriptor: string): OracleDescriptorAddress | null {
  const nodes = parseDescriptor(descriptor);
  if (!nodes || nodes.length !== 1) return null;
  const [top] = nodes;
  const description = top.key === 'DESCRIPTION' ? top : top.key === 'DESCRIPTION_LIST' ? top.children?.find((child) => child.key === 'DESCRIPTION') : undefined;
  return description ? addressOf(description) : null;
}

/**
 * An Oracle connect descriptor as `host:port/service` (or `host:port SID sid`) — its first address and its service —
 * never the descriptor itself, which can carry credentials (`(SECURITY=(MY_WALLET_PASSWORD=…))`). "TNS descriptor"
 * when it names no host or cannot be read without guessing.
 */
export function describeOracleDescriptor(descriptor: string): string {
  const address = oracleDescriptorAddress(descriptor);
  if (!address) return 'TNS descriptor';
  const { host, port, service, sid } = address;
  return `${host}${port ? `:${port}` : ''}${service ? `/${service}` : sid ? ` SID ${sid}` : ''}`;
}
